'use client';

import type {ExperienceGate} from 'common/experiments';
import {useAuth} from 'iq-auth';
import {useSubscribeHref} from 'iq-upselling/hooks';
import {
  LockedReportRows,
  PremiumPriceLine,
  usePricingHref,
} from 'iq-interview_experiences/components/experience_gate_card';
import {SubmitExperienceModal} from 'iq-interview_experiences/components/submit_experience_modal';
import {
  trackSubmitExperienceOpened,
  trackSubscribeClicked,
} from 'iq-interview_experiences/tracking';
import type {InterviewExperience} from 'iq-interview_experiences/types';
import {
  trackGuideExperienceSubscribeClicked,
  trackGuideInlinePaywallViewed,
} from 'iq-interview_guides/tracking';
import {InterviewGuideData} from 'iq-interview_guides/types';
import {Button} from '@repo/ui/button';
import {useEffect, useState} from 'react';
import {useParams} from 'next/navigation';

interface ExperiencesPaywallProps {
  companySlug: InterviewGuideData['company_slug'];
  positionSlug: InterviewGuideData['position_slug'];
  companyName: InterviewGuideData['company_name'];
  positionName: InterviewGuideData['position_name'];
  totalCount: number;
  companyId: InterviewGuideData['company_id'];
  positionId: InterviewGuideData['position_id'];
  lockStatus: InterviewGuideData['lock_status'];
  experienceShared: InterviewGuideData['experience_shared'];
  lockedExperiences: InterviewExperience[];
  /** Set when this page view is in guide_anon_experience_gate_v1. */
  experienceGate: ExperienceGate | null;
}

type PaywallCardProps = Omit<ExperiencesPaywallProps, 'experienceShared'>;

// Split out from the wrapper below so the impression effect only runs once the paywall
// has actually cleared the visibility guards and rendered.
function ExperiencesPaywallCard({
  companySlug,
  positionSlug,
  companyName,
  positionName,
  companyId,
  positionId,
  totalCount,
  lockStatus,
  lockedExperiences,
  experienceGate,
}: PaywallCardProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const subscribeHref = useSubscribeHref();
  const pricingHref = usePricingHref();
  const params = useParams();
  const slug = typeof params?.slug === 'string' ? params.slug : '';

  // On a `locked` guide this card sits under the full-page gate, which reports its own
  // impression, so only count the inline surface when it is the one the visitor sees.
  useEffect(() => {
    if (lockStatus !== 'partially-unlocked') return;
    void trackGuideInlinePaywallViewed({slug});
  }, [slug, lockStatus]);

  function handleShareClick() {
    void trackSubmitExperienceOpened('barrier');
    setModalOpen(true);
  }

  function trackExperimentSubscribe() {
    if (!experienceGate) return;
    void trackGuideExperienceSubscribeClicked({
      slug,
      variant: experienceGate.variant,
      gateMode: experienceGate.gateMode,
    });
  }

  // guide_anon_experience_gate_v1, experiment arm, logged-in free users: the first report
  // stays readable, and this card lists the locked ones with the Premium price anchor.
  // Anonymous visitors in the experiment arm get ExperienceGateCard instead of this card.
  const upgraded = experienceGate?.variant === 'experiment';

  return (
    <>
      <div className='mt-4 bg-gray-50 border border-iq-grey-light rounded-lg p-8 text-center'>
        <h3 className='text-lg font-bold text-iq-black mb-2'>
          {upgraded
            ? `${totalCount} interview reports from ${companyName} ${positionName} candidates`
            : `Unlock all ${totalCount} interview reports`}
        </h3>
        <p className='text-sm text-iq-grey-medium mb-6'>
          Share your own interview experience to unlock all reports, or subscribe for full access.
        </p>
        {upgraded && (
          <div className='mb-6'>
            <LockedReportRows experiences={lockedExperiences} positionName={positionName} />
          </div>
        )}
        <div className='flex items-center justify-center gap-3 flex-wrap'>
          <Button
            onClick={handleShareClick}
            className='bg-iq-orange text-white hover:bg-iq-orange hover:opacity-90'>
            Share my experience
          </Button>
          <Button
            asChild
            variant='outline'
            onClick={() => {
              void trackSubscribeClicked('barrier');
              trackExperimentSubscribe();
            }}
            className='border-iq-orange text-iq-orange hover:bg-iq-orange-light hover:text-iq-orange'>
            <a href={upgraded ? pricingHref : subscribeHref}>Subscribe to IQ Premium</a>
          </Button>
        </div>
        {upgraded && (
          <div className='mt-4'>
            <PremiumPriceLine totalCount={totalCount} onClick={trackExperimentSubscribe} />
          </div>
        )}
      </div>

      {modalOpen && (
        <SubmitExperienceModal
          guideSlug={slug}
          onClose={() => setModalOpen(false)}
          defaultCompanySlug={companySlug}
          defaultPositionSlug={positionSlug}
          companyId={companyId}
          positionId={positionId}
        />
      )}
    </>
  );
}

export default function ExperiencesPaywall({
  experienceShared,
  ...cardProps
}: ExperiencesPaywallProps) {
  const {totalCount, lockStatus} = cardProps;
  const {isPremium} = useAuth();

  // Hide when there is nothing to unlock (the empty state is the only CTA then),
  // the user has a subscription, or the guide was unlocked via experience submission.
  if (totalCount === 0 || isPremium || lockStatus === 'fully-unlocked' || experienceShared)
    return null;

  return <ExperiencesPaywallCard {...cardProps} />;
}
